import { Button } from '@veneer/core';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import imageResumeCanvas from '../../assets/images/resume-canvas.png';
import imageResumeIcon from '../../assets/images/resume-icon.svg';
import imageResumePhotobook from '../../assets/images/resume-photobook.png';
import { getProjectExpirationText } from '../../helpers/expirationDate';
import { ScreenInfo, useDataCollection } from '../../jarvis/dataCollection';
import ModalDevice from '../Modals/Device';
import './index.css';

export interface ResumeProps {
  projectId: string;
  projectCategory: string;
  userMediaclipToken: string;
  projectExpirationDate: string;
  projectAppInstanceId: string;
  projectHasLocalPhotos: boolean;
  activeAppInstanceId: string;
  isTester: boolean;
  onClickLaunchDesigner: () => void;
  onClickResetDesigner: () => void;
  onClickResetUser: () => void;
}

const Resume = (props: ResumeProps) => {
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const dataCollection = useDataCollection();

  const screenInfo: ScreenInfo = {
    name: 'ResumeProject',
    activity: 'PhotobookResumeProject-v01',
  };

  const continueButtonName = 'ContinueButton';
  const startOverButtonName = 'StartOverButton';

  const sendClickEvent = (controlName: string) => {
    dataCollection.sendControlClickedEvent({ name: controlName, detail: getWebAppVersion() }, screenInfo);
  };

  const getWebAppVersion = () => process.env['REACT_APP_PACKAGE_VERSION'];

  const history = useHistory();

  const handleContinueClick = () => {
    if (props.projectHasLocalPhotos && props.activeAppInstanceId !== props.projectAppInstanceId) {
      return setShowDeviceModal(true);
    }
    sendClickEvent(continueButtonName);
    props.onClickLaunchDesigner();
  };

  const handleStarOverClick = () => {
    sendClickEvent(startOverButtonName);
    props.onClickResetDesigner();
  };

  const onClickTesterListOrders = () => {
    history.push('/orders');
  };

  const onClickTesterListProject = () => {
    history.push('/projects');
  };

  const onClickTesterResetUser = () => {
    props.onClickResetUser();
  };

  const categoryDescription: {
    [index: string]: JSX.Element;
  } = {
    photobook: (
      <div className="description">
        <p>Your personalized HP Photobook is almost complete. Finish today to share your memories.</p>
        <p>If you’d like to delete your previous photobook and begin a new one, select Start Over.</p>
      </div>
    ),
    canvas: (
      <div className="description">
        <p>Your personalized HP Canvas is almost complete. Finish today to share your memories.</p>
        <p>If you’d like to delete your previous canvas and begin a new one, select Start Over.</p>
      </div>
    ),
  };

  const categoryImage: {
    [index: string]: string;
  } = {
    photobook: imageResumePhotobook,
    canvas: imageResumeCanvas,
  };

  return (
    <>
      <div className="ResumePage page">
        <div className={`resumeBanner ${props.projectCategory}`}>
          <img src={categoryImage[props.projectCategory]} className="resumeImage" />
          <div className="iconEllipse">
            <img src={imageResumeIcon} className="imageResumeIcon" />
          </div>
        </div>
        <div className="info">
          <h1 className="title-small">Welcome back</h1>
          {categoryDescription[props.projectCategory]}
          <div className="buttons">
            <Button onClick={handleContinueClick}>Continue</Button>
            {props.projectExpirationDate && (
              <span>({getProjectExpirationText(props.projectExpirationDate)} remaining)</span>
            )}
            <Button appearance="secondary" onClick={handleStarOverClick}>
              Start Over
            </Button>
            {props.isTester && (
              <div className="testArea">
                <Button appearance="negative" onClick={onClickTesterListOrders}>
                  List Orders
                </Button>
                <Button appearance="negative" onClick={onClickTesterListProject}>
                  List Projects
                </Button>
                <Button appearance="negative" onClick={onClickTesterResetUser}>
                  Reset User
                </Button>
                <span className="debug-disclaimer caption-small">* You are configured as a tester.</span>
              </div>
            )}
          </div>
        </div>
      </div>
      <ModalDevice
        showDeviceModal={showDeviceModal}
        expiresAt={props.projectExpirationDate}
        setShowDeviceModal={setShowDeviceModal}
      />
    </>
  );
};

export default Resume;
